import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ListSkeleton } from '../../components/ui/list-skeleton';
import { 
  Package, Users, Activity, TrendingUp, AlertTriangle, Clock, 
  ChevronRight, Play, Pause, CheckCircle, XCircle, Plus,
  Calendar, DollarSign, Eye, Building2, Wrench, Calculator
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GestorDashboard() {
  const navigate = useNavigate();
  const [metricas, setMetricas] = useState(null);
  const [ofsUrgentes, setOfsUrgentes] = useState([]);
  const [ofsEmCurso, setOfsEmCurso] = useState([]);
  const [anomaliasAbertas, setAnomaliasAbertas] = useState([]);
  const [anomaliasPorTipo, setAnomaliasPorTipo] = useState([]);
  const [tiposAnomalia, setTiposAnomalia] = useState([]);
  const [operadoresAtivos, setOperadoresAtivos] = useState([]);
  const [alertasContagem, setAlertasContagem] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
    // Auto-refresh a cada 15 segundos
    const interval = setInterval(loadDashboard, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboard = async () => {
    try {
      const [metricasRes, ofsRes, anomaliasRes, tiposRes, estadosRes] = await Promise.all([
        axios.get(`${API}/dashboard/metricas`),
        axios.get(`${API}/ofs`),
        axios.get(`${API}/anomalias-producao`),
        axios.get(`${API}/tipos-anomalia`),
        axios.get(`${API}/operadores/estados`).catch(() => ({ data: [] }))
      ]);
      
      setMetricas(metricasRes.data);
      setTiposAnomalia(tiposRes.data);
      
      // Operadores ativos (online + pausa)
      const ativos = (estadosRes.data || []).filter(op => op.estado === 'online' || op.estado === 'pausa');
      setOperadoresAtivos(ativos);
      
      // OFs urgentes (próximas da data de entrega ou atrasadas)
      const hoje = new Date().toISOString().split('T')[0];
      const urgentes = ofsRes.data
        .filter(of => of.data_entrega && of.data_entrega <= hoje && of.status !== 'terminado' && of.status !== 'cancelado')
        .slice(0, 5);
      setOfsUrgentes(urgentes);
      
      // OFs em curso
      const emCurso = ofsRes.data
        .filter(of => of.status === 'em_curso' || of.status === 'pausado')
        .slice(0, 5);
      setOfsEmCurso(emCurso);
      
      // Anomalias pendentes (não arquivadas e não totalmente resolvidas)
      const pendentes = anomaliasRes.data.filter(a => 
        a.status !== 'arquivado' && a.status !== 'descartado' && a.status !== 'recuperado'
      );
      setAnomaliasAbertas(pendentes.slice(0, 5));
      
      // Agrupar anomalias por tipo
      const tiposMap = {};
      tiposRes.data.forEach(t => { tiposMap[t.id] = t; });
      
      const contadorPorTipo = {};
      pendentes.forEach(a => {
        const tipoId = a.tipo_anomalia_id || 'outro';
        if (!contadorPorTipo[tipoId]) {
          const tipoInfo = tiposMap[tipoId] || { nome: 'Outro', cor: '#6b7280' };
          contadorPorTipo[tipoId] = { 
            id: tipoId,
            nome: tipoInfo.nome, 
            cor: tipoInfo.cor, 
            count: 0 
          };
        }
        contadorPorTipo[tipoId].count++;
      });
      
      // Ordenar por quantidade (maior primeiro)
      const agrupados = Object.values(contadorPorTipo).sort((a, b) => b.count - a.count);
      setAnomaliasPorTipo(agrupados);
      
      // Carregar alertas de contagem não resolvidos (só com diferença != 0)
      try {
        const alertasRes = await axios.get(`${API}/alertas-contagem?resolvido=false`);
        // Filtrar alertas com diferença 0
        const alertasFiltrados = (alertasRes.data || []).filter(a => a.diferenca !== 0);
        setAlertasContagem(alertasFiltrados);
      } catch (e) {
        console.warn('Erro ao carregar alertas de contagem:', e);
        setAlertasContagem([]);
      }
      
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Resolver alerta de contagem
  const handleResolverAlerta = async (alertaId) => {
    try {
      await axios.put(`${API}/alertas-contagem/${alertaId}/resolver`);
      toast.success('Alerta resolvido!');
      setAlertasContagem(prev => prev.filter(a => a.id !== alertaId));
    } catch (error) {
      toast.error('Erro ao resolver alerta');
    }
  };

  const getProgressColor = (progresso) => {
    if (progresso >= 80) return 'bg-sage';
    if (progresso >= 50) return 'bg-sand';
    return 'bg-terracotta';
  };

  if (loading) {
    return (
      <GestorLayout>
        <div className="space-y-6">
          {/* Header (mesma estrutura, sem skeleton para manter navegação estável) */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold text-stone-900">Dashboard</h1>
              <p className="text-stone-500">{new Date().toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
          {/* Placeholders com a forma das métricas e cards reais */}
          <ListSkeleton items={4} variant="metric" />
          <ListSkeleton items={3} variant="card" />
        </div>
      </GestorLayout>
    );
  }

  return (
    <GestorLayout>
      <div className="space-y-6">
        {/* Header com ações rápidas */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900">Dashboard</h1>
            <p className="text-stone-500">{new Date().toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/gestor/ofs')} variant="outline" className="rounded-full">
              <Eye className="h-4 w-4 mr-2" /> Ver OFs
            </Button>
            <Button onClick={() => navigate('/gestor/ofs')} className="rounded-full">
              <Plus className="h-4 w-4 mr-2" /> Nova OF
            </Button>
          </div>
        </div>

        {/* Alertas Urgentes */}
        {(ofsUrgentes.length > 0 || anomaliasAbertas.length > 0 || alertasContagem.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {ofsUrgentes.length > 0 && (
              <Card className="p-4 border-l-4 border-l-terracotta bg-terracotta/5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-terracotta/20 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-terracotta" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">OFs Urgentes</h3>
                    <p className="text-sm text-stone-500">{ofsUrgentes.length} entregas atrasadas ou para hoje</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {ofsUrgentes.slice(0, 3).map(of => (
                    <div key={of.id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                      <div>
                        <span className="font-mono font-bold text-sm">{of.numero_of}</span>
                        <span className="text-stone-500 text-sm ml-2">{of.cliente}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-terracotta font-medium">{of.data_entrega ? new Date(of.data_entrega).toLocaleDateString('pt-PT') : ''}</span>
                        <Button size="sm" variant="ghost" onClick={() => navigate('/gestor/ofs')}>
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            
            {anomaliasAbertas.length > 0 && (
              <Card className="p-4 border-l-4 border-l-amber-500 bg-amber-50">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">Anomalias por Resolver</h3>
                    <p className="text-sm text-stone-500">{anomaliasAbertas.length} ocorrência(s) aberta(s)</p>
                  </div>
                </div>
                
                {/* Divisão por tipo de anomalia */}
                {anomaliasPorTipo.length > 0 && (
                  <div className="mb-3 space-y-2">
                    {anomaliasPorTipo.slice(0, 4).map(tipo => (
                      <div key={tipo.id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: tipo.cor }}
                          />
                          <span className="text-sm text-stone-700">{tipo.nome}</span>
                        </div>
                        <span className="text-sm font-bold" style={{ color: tipo.cor }}>
                          {tipo.count}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                
                <Button size="sm" onClick={() => navigate('/gestor/anomalias')} className="w-full">
                  Ver Anomalias
                </Button>
              </Card>
            )}
            
            {/* Card de Alertas de Contagem */}
            {alertasContagem.length > 0 && (
              <Card className="p-4 border-l-4 border-l-blue-500 bg-blue-50" data-testid="alertas-contagem-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Calculator className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">Alertas de Contagem</h3>
                    <p className="text-sm text-stone-500">{alertasContagem.length} discrepância(s) detectada(s)</p>
                  </div>
                </div>
                
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {alertasContagem.map(alerta => (
                    <div 
                      key={alerta.id} 
                      className="flex items-center justify-between p-2 bg-white rounded-lg border border-blue-100 cursor-pointer hover:shadow-sm transition-shadow"
                      onClick={() => navigate('/gestor/alertas')}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                            alerta.diferenca < 0 ? 'bg-red-100 text-red-700' : 
                            alerta.diferenca > 0 ? 'bg-green-100 text-green-700' : 
                            'bg-stone-100 text-stone-700'
                          }`}>
                            {alerta.diferenca < 0 ? 'Falta' : alerta.diferenca > 0 ? 'Excesso' : 'Igual'}
                          </span>
                          {alerta.of_numero && (
                            <span className="text-xs font-bold text-navy">OF {alerta.of_numero}</span>
                          )}
                          {alerta.marca && (
                            <span className="text-xs text-stone-500">{alerta.marca}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap mt-0.5">
                          {alerta.lote_codigo && (
                            <span className="font-mono text-xs text-stone-600">
                              Lote: {alerta.lote_codigo}
                            </span>
                          )}
                          {alerta.operacao_codigo && (
                            <span className="text-xs text-stone-500">
                              {alerta.operacao_codigo} {alerta.operacao_nome || ''}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-stone-700 mt-1">
                          Esperado: <strong>{alerta.quantidade_esperada}</strong> | 
                          Contado: <strong>{alerta.quantidade_contada}</strong>
                          <span className={`ml-2 font-bold ${alerta.diferenca < 0 ? 'text-red-600' : alerta.diferenca > 0 ? 'text-green-600' : 'text-stone-500'}`}>
                            ({alerta.diferenca > 0 ? '+' : ''}{alerta.diferenca})
                          </span>
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={(e) => { e.stopPropagation(); handleResolverAlerta(alerta.id); }}
                        className="text-blue-600 hover:bg-blue-100"
                        title="Marcar como resolvido"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                
                {alertasContagem.length > 10 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-2 text-xs"
                    onClick={() => navigate('/gestor/alertas')}
                  >
                    Ver todos os alertas ({alertasContagem.length})
                  </Button>
                )}
              </Card>
            )}
          </div>
        )}

        {/* KPIs Principais */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-5 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/gestor/ofs')} data-testid="metrica-total-ofs">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                <Package className="h-5 w-5 text-primary" />
              </div>
              <span className="text-xs text-stone-500 uppercase tracking-wider">Total OFs</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">{metricas?.total_ofs || 0}</p>
          </Card>

          <Card className="p-5 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/gestor/ofs')} data-testid="metrica-ofs-andamento">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-sage/10 flex items-center justify-center">
                <Play className="h-5 w-5 text-sage" />
              </div>
              <span className="text-xs text-stone-500 uppercase tracking-wider">Em Curso</span>
            </div>
            <p className="text-3xl font-bold text-sage">{metricas?.ofs_em_andamento || 0}</p>
          </Card>

          <Card className="p-5 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/gestor/operadores')} data-testid="metrica-operadores">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-sand/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-sand" />
              </div>
              <span className="text-xs text-stone-500 uppercase tracking-wider">Operadores</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">{metricas?.total_operadores || 0}</p>
          </Card>

          <Card className="p-5 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/gestor/financeiro')} data-testid="metrica-eficiencia">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-terracotta/10 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-terracotta" />
              </div>
              <span className="text-xs text-stone-500 uppercase tracking-wider">Eficiência</span>
            </div>
            <p className="text-3xl font-bold text-terracotta">{metricas?.eficiencia_media || 0}%</p>
          </Card>
        </div>

        {/* Operadores Ativos — Visão em tempo real */}
        {operadoresAtivos.length > 0 && (
          <Card className="p-5 border-l-4 border-l-green-500">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-green-600" /> Operadores Ativos
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">{operadoresAtivos.length}</span>
              </h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/gestor/estado-operadores')}>
                Ver todos <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {operadoresAtivos.slice(0, 6).map(op => (
                <div
                  key={op.operador_id}
                  className={`p-3 rounded-xl border-2 transition-all hover:shadow-md ${
                    op.estado === 'pausa'
                      ? 'border-orange-300 bg-orange-50'
                      : 'border-green-300 bg-green-50'
                  }`}
                  data-testid={`dashboard-operador-${op.username}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                      op.estado === 'pausa' ? 'bg-orange-500' : 'bg-green-600'
                    }`}>
                      {op.nome?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate text-stone-800">{op.nome}</p>
                      {op.operacao_atual ? (
                        <p className="text-xs text-green-700 font-medium truncate">
                          {op.operacao_atual_codigo ? `${op.operacao_atual_codigo} — ` : ''}{op.operacao_atual}
                        </p>
                      ) : op.estado === 'pausa' ? (
                        <p className="text-xs text-orange-600 font-medium flex items-center gap-1">
                          <Pause className="h-3 w-3" /> Em pausa
                        </p>
                      ) : null}
                    </div>
                    <div className="text-right flex-shrink-0">
                      {op.eficiencia_dia > 0 && (
                        <span className={`text-sm font-bold ${
                          op.eficiencia_dia >= 100 ? 'text-green-600' : op.eficiencia_dia >= 80 ? 'text-amber-600' : 'text-red-600'
                        }`}>{Math.round(op.eficiencia_dia)}%</span>
                      )}
                      {op.pecas_hoje > 0 && (
                        <p className="text-[10px] text-stone-400">{op.pecas_hoje} pç</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* OFs em Curso */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <Activity className="h-5 w-5 text-sage" /> Em Produção
              </h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/gestor/ofs')}>
                Ver todas <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            {ofsEmCurso.length === 0 ? (
              <div className="text-center py-8 text-stone-500">
                <Package className="h-12 w-12 mx-auto mb-3 text-stone-300" />
                <p>Nenhuma OF em produção</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ofsEmCurso.map(of => {
                  // Usar progresso_real se disponível, senão calcular baseado em peças
                  const progresso = of.progresso_real ?? Math.round(((of.quantidade_produzida || 0) / (of.quantidade_total || 1)) * 100);
                  return (
                    <div key={of.id} className="p-3 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-mono font-bold">{of.numero_of}</span>
                          <span className="text-stone-500 text-sm ml-2">{of.cliente}</span>
                        </div>
                        <span className="text-sm font-semibold">{progresso}%</span>
                      </div>
                      <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getProgressColor(progresso)} transition-all`}
                          style={{ width: `${progresso}%` }}
                        />
                      </div>
                      <p className="text-xs text-stone-500 mt-1">
                        {of.operacoes_concluidas || 0} / {of.total_operacoes_lotes || of.operacoes?.length || 0} operações
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Ações Rápidas */}
          <Card className="p-5">
            <h2 className="font-heading font-semibold text-lg mb-4">Ações Rápidas</h2>
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                className="h-20 flex-col gap-2 rounded-xl hover:bg-primary/5 hover:border-primary"
                onClick={() => navigate('/gestor/ofs')}
              >
                <Plus className="h-6 w-6 text-primary" />
                <span className="text-sm">Nova OF</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex-col gap-2 rounded-xl hover:bg-sage/5 hover:border-sage"
                onClick={() => navigate('/gestor/operadores')}
              >
                <Users className="h-6 w-6 text-sage" />
                <span className="text-sm">Operadores</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex-col gap-2 rounded-xl hover:bg-sand/5 hover:border-sand"
                onClick={() => navigate('/gestor/marcas?new=1')}
              >
                <Building2 className="h-6 w-6 text-sand" />
                <span className="text-sm">Nova Marca</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex-col gap-2 rounded-xl hover:bg-terracotta/5 hover:border-terracotta"
                onClick={() => navigate('/gestor/operacoes?new=1')}
              >
                <Wrench className="h-6 w-6 text-terracotta" />
                <span className="text-sm">Nova Operação</span>
              </Button>
            </div>
          </Card>
        </div>

        {/* Anomalias */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-terracotta" /> Anomalias Recentes
            </h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/gestor/anomalias')}>
              Ver todas <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          
          {/* Resumo de anomalias por tipo */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-3 bg-red-50 rounded-xl border border-red-200">
              <div className="flex items-center gap-2 mb-1">
                <XCircle className="h-4 w-4 text-red-500" />
                <span className="text-sm font-semibold text-red-700">Defeitos</span>
              </div>
              <p className="text-2xl font-bold text-red-600">
                {anomaliasAbertas.filter(a => a.tipo?.toLowerCase().includes('defeito') || a.tipo === 'qualidade').length}
              </p>
              <p className="text-xs text-red-500">Problemas de qualidade</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-xl border border-orange-200">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                <span className="text-sm font-semibold text-orange-700">Perdas</span>
              </div>
              <p className="text-2xl font-bold text-orange-600">
                {anomaliasAbertas.filter(a => a.tipo?.toLowerCase().includes('perda') || a.tipo === 'material' || a.tipo === 'desperdicio').length}
              </p>
              <p className="text-xs text-orange-500">Material perdido</p>
            </div>
          </div>

          {/* Lista de anomalias abertas */}
          {anomaliasAbertas.length === 0 ? (
            <div className="text-center py-4 text-stone-500 bg-green-50 rounded-xl">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <p className="text-sm text-green-600 font-medium">Nenhuma anomalia aberta</p>
            </div>
          ) : (
            <div className="space-y-2">
              {anomaliasAbertas.slice(0, 4).map(anomalia => (
                <div key={anomalia.id} className="p-2 bg-stone-50 rounded-lg flex items-center justify-between hover:bg-stone-100 transition-colors">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      anomalia.tipo?.toLowerCase().includes('defeito') || anomalia.tipo === 'qualidade'
                        ? 'bg-red-500' 
                        : 'bg-orange-500'
                    }`} />
                    <div>
                      <p className="text-sm font-medium text-stone-800">{anomalia.tipo}</p>
                      <p className="text-xs text-stone-500 truncate max-w-[200px]">{anomalia.descricao}</p>
                    </div>
                  </div>
                  <span className="text-xs text-stone-400">
                    {anomalia.criado_em ? new Date(anomalia.criado_em).toLocaleDateString('pt-PT') : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Resumo por Estado */}
        <Card className="p-5">
          <h2 className="font-heading font-semibold text-lg mb-4">Resumo por Estado</h2>
          <div className="grid grid-cols-3 md:grid-cols-7 gap-3">
            {[
              { label: 'Rascunho', count: metricas?.ofs_rascunho || 0, color: 'bg-stone-100 text-stone-600', icon: Clock },
              { label: 'Ready', count: metricas?.ofs_pendentes || 0, color: 'bg-blue-100 text-blue-600', icon: CheckCircle },
              { label: 'Aguardar', count: metricas?.ofs_aguardar_entrada || 0, color: 'bg-amber-100 text-amber-600', icon: Clock },
              { label: 'Em Curso', count: metricas?.ofs_em_curso || 0, color: 'bg-sage/20 text-sage', icon: Play },
              { label: 'Pausado', count: metricas?.ofs_pausadas || 0, color: 'bg-sand/20 text-sand', icon: Pause },
              { label: 'Terminado', count: metricas?.ofs_terminadas || 0, color: 'bg-green-100 text-green-600', icon: CheckCircle },
              { label: 'Cancelado', count: metricas?.ofs_canceladas || 0, color: 'bg-red-100 text-red-600', icon: XCircle },
            ].map(item => {
              const Icon = item.icon;
              return (
                <div 
                  key={item.label} 
                  className={`p-4 rounded-xl text-center ${item.color} cursor-pointer hover:scale-105 transition-transform`}
                  onClick={() => navigate('/gestor/ofs')}
                >
                  <Icon className="h-5 w-5 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{item.count}</p>
                  <p className="text-xs">{item.label}</p>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </GestorLayout>
  );
}
